import React from 'react';
import { MapPin, Camera, Bus, Calendar, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function SurroundingTours() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.surroundingTours.title')}
      subtitle={t('services.surroundingTours.subtitle')}
      icon={MapPin}
      heroColor="from-emerald-500 to-green-600"
      enquiryTitle={t('services.surroundingTours.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.surroundingTours.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.surroundingTours.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.surroundingTours.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-customized">
          <CardContent className="p-6">
            <Camera className="w-12 h-12 text-emerald-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.surroundingTours.features.customized.title')}</h3>
            <p className="text-gray-700">
              {t('services.surroundingTours.features.customized.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-transport">
          <CardContent className="p-6">
            <Bus className="w-12 h-12 text-emerald-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.surroundingTours.features.transport.title')}</h3>
            <p className="text-gray-700">
              {t('services.surroundingTours.features.transport.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-flexible">
          <CardContent className="p-6">
            <Calendar className="w-12 h-12 text-emerald-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.surroundingTours.features.flexible.title')}</h3>
            <p className="text-gray-700">
              {t('services.surroundingTours.features.flexible.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-guides">
          <CardContent className="p-6">
            <MapPin className="w-12 h-12 text-emerald-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.surroundingTours.features.guides.title')}</h3>
            <p className="text-gray-700">
              {t('services.surroundingTours.features.guides.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Popular Tours */}
      <Card className="mb-8 shadow-lg" data-testid="popular-tours-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.surroundingTours.popularTours')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.surroundingTours.toursList').map((tour) => (
              <div key={tour} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{tour}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tour Types */}
      <Card className="mb-8 shadow-lg" data-testid="tour-types-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.surroundingTours.typesTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.surroundingTours.types.halfDay.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.surroundingTours.types.halfDay.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.surroundingTours.types.halfDay.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.surroundingTours.types.fullDay.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.surroundingTours.types.fullDay.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.surroundingTours.types.fullDay.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.surroundingTours.types.multiDay.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.surroundingTours.types.multiDay.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.surroundingTours.types.multiDay.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
